import { useLocale } from 'locale';
import React, { useState } from 'react';
import { Text, H3, HrefText, SmallText } from 'ds/components/typography';
import { UserRatings } from 'utils/entities';
import {
  UserRating,
  UserRatingBarContainer,
  UserRatingContainer,
  SingleRatingContainer,
  UserRatingsContainer,
  RatingsContainer,
  SectionWrapper,
  SectionHeader,
  SectionSubtitle,
} from '../styled';

import CheckmarkIcon from 'assets/svg/user-rating/v-1-5.svg';
import CarIcon from 'assets/svg/user-rating/private-car.svg';
import MaintenanceIcon from 'assets/svg/user-rating/maintenance.svg';
import ParkingIcon from 'assets/svg/user-rating/parking.svg';
import ParksIcon from 'assets/svg/user-rating/parks.svg';
import PedestriansIcon from 'assets/svg/user-rating/pedestrians.svg';
import PreschoolIcon from 'assets/svg/user-rating/preschool.svg';
import SchoolsIcon from 'assets/svg/user-rating/schools.svg';
import ShoppingIcon from 'assets/svg/user-rating/shopping.svg';
import TransportationIcon from 'assets/svg/user-rating/transportation.svg';
import ActivityIcon from 'assets/svg/user-rating/activity.svg';
import CommunityIcon from 'assets/svg/user-rating/community.svg';
import ShieldIcon from 'assets/svg/user-rating/shield.svg';
import { Collapsable } from 'components/filters/common/Collapsable';
import { ShowAt } from 'consts/breakpoints';
import styled from '@emotion/styled';

const ShowMore = styled(SmallText)`
  margin: ${({ theme }) => `${theme.spacing(3)} 0 ${theme.spacing(1)}`};
  ${({ theme }) => theme.media.showAt({ at: 1 }) `
    margin-bottom: ${theme.spacing(2)};
  `}
  cursor: pointer;
`;

interface NeighboursProps {
  userRatings: { [rating in UserRatings]?: number | boolean };
}

type RatedKey = 'cleanRank' | 'communityRank' | 'kindergartenRank' | 'parkingRank' | 'parksRank' | 'pastimeRank' | 'publicTransportRank' | 'safetyRank' | 'schoolsRank' | 'shoppingRank' | 'transportRank' | 'walkabilityRank';

const ratedKeys: RatedKey[] = [
  'cleanRank',
  'communityRank',
  'kindergartenRank',
  'parkingRank',
  'parksRank',
  'pastimeRank',
  'publicTransportRank',
  'safetyRank',
  'schoolsRank',
  'shoppingRank',
  'transportRank',
  'walkabilityRank',
];

const ratedKeyToIcon: Record<RatedKey, JSX.Element> = {
  'cleanRank': <MaintenanceIcon />,
  'communityRank': <CommunityIcon />,
  'kindergartenRank': <PreschoolIcon />,
  'parkingRank': <ParkingIcon />,
  'parksRank': <ParksIcon />,
  'pastimeRank': <ActivityIcon />,
  'publicTransportRank': <CarIcon />,
  'safetyRank': <ShieldIcon />,
  'schoolsRank': <SchoolsIcon />,
  'shoppingRank': <ShoppingIcon />,
  'transportRank': <TransportationIcon />,
  'walkabilityRank': <PedestriansIcon />,
};

const suitableRatingsKeys = [ 'suitableForBachelor', 'suitableForFamilies', 'suitableForNonReligious', 'suitableForOrthodox', 'suitableForPensioner', 'suitableForReligious' ];

export const Neighbours: React.FC<NeighboursProps> = ({ userRatings }) => {
  const { t } = useLocale();
  const [ isCollapsed, setIsCollapsed ] = useState(true);
  return (
    <SectionWrapper>
      {userRatings ? (
        <>
          <SectionHeader>
            <H3 weight="bold">{t('localPage.neighbours.ratings.title')}</H3>
            <SectionSubtitle>{t('localPage.neighbours.ratings.subtitle', { amount: userRatings.rankingsCount })}</SectionSubtitle>
          </SectionHeader>
          <RatingsContainer data-auto="ratings-container">
            <SectionSubtitle>
              {t('localPage.neighbours.ratings.suitable.title')}
            </SectionSubtitle>
            {suitableRatingsKeys.map(k =>
              userRatings[k] ? (
                <SingleRatingContainer data-auto="single-rating" weight="medium" key={k}>
                  <CheckmarkIcon />
                  {t('localPage.neighbours.ratings.suitable', { ratingKey: k })}
                </SingleRatingContainer>
              ) : null
            )}
          </RatingsContainer>
          <UserRatingsContainer data-auto="user-ratings-container">
            <ShowAt at={1}>
              {isMobile => (
                <Collapsable
                  isCollapsed={isCollapsed}
                  onClick={() => setIsCollapsed(false)}
                  collapsedHeight={isMobile ? 224 : 118}
                >
                  {ratedKeys.map((k =>
                    userRatings[k] ? (
                      <UserRatingContainer data-auto="user-rating" key={k}>
                        {ratedKeyToIcon[k]}
                        <Text weight="medium">{t('localPage.neighbours.ratings.rated', { ratingKey: k })}</Text>
                        <UserRatingBarContainer>
                          <UserRating ratingPercentage={(userRatings[k] as number) * 20} />
                        </UserRatingBarContainer>
                        <Text weight="bold">{(userRatings[k] as number).toFixed(1)}</Text>
                      </UserRatingContainer>
                    ) : null
                  ))}
                </Collapsable>
              )}
            </ShowAt>
            {isCollapsed ? (
              <ShowMore weight="medium" onClick={() => setIsCollapsed(false)}>
                <HrefText>{t('localPage.neighbours.ratings.showMore')}</HrefText>
              </ShowMore>
            ) : null}
          </UserRatingsContainer>
        </>
      ) : null}
    </SectionWrapper>
  );
};
